import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  /** После первого loadProfile не поднимаем глобальный loading (TOKEN_REFRESHED и т.д.) */
  const profileBootstrapDoneRef = useRef(false)

  useEffect(() => {
    // Проверяем текущую сессию
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        loadProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Слушаем изменения аутентификации
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] Auth state changed:', event)
      setUser(session?.user ?? null)
      if (session?.user) {
        const silent =
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        await loadProfile(session.user.id, { silent })
      } else {
        setProfile(null)
        setLoading(false)
        profileBootstrapDoneRef.current = false
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const loadProfile = async (userId, options = {}) => {
    const silent = options.silent === true || profileBootstrapDoneRef.current
    try {
      console.log('[AuthContext] Loading profile for user:', userId, silent ? '(silent)' : '')
      if (!silent) {
        setLoading(true)
      }
      
      // Добавляем таймаут для запроса профиля (10 секунд)
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile load timeout')), 6000)
      })

      console.log('[AuthContext] Starting profile query...')
      const startTime = Date.now()
      
      const { data, error } = await Promise.race([profilePromise, timeoutPromise])
      
      const elapsed = Date.now() - startTime
      console.log(`[AuthContext] Profile query completed in ${elapsed}ms`)

      if (error) {
        console.error('[AuthContext] Error loading profile:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        })
        
        // Если профиль не найден - это ошибка, не создаем минимальный профиль
        if (error.code === 'PGRST116') {
          console.error('[AuthContext] ❌ Profile not found in database! User ID:', userId)
          console.error('[AuthContext] This should not happen after registration. Check if profile was saved correctly.')
          setProfile(null)
        } else {
          console.error('[AuthContext] ❌ Error loading profile:', error.message)
          setProfile(null)
        }
      } else {
        if (data) {
          console.log('[AuthContext] ✅ Profile loaded successfully:', { 
            id: data?.id, 
            name: data?.name,
            gender: data?.gender,
            looking_for: data?.looking_for,
            date_of_birth: data?.date_of_birth,
            photosCount: data?.photos?.length || 0,
            hasAllRequiredFields: !!(data.name && data.gender && data.looking_for && data.date_of_birth)
          })
          
          // Проверяем, что профиль не пустой
          if (!data.name || !data.gender || !data.looking_for || !data.date_of_birth) {
            console.warn('[AuthContext] ⚠️ Profile loaded but incomplete:', {
              hasName: !!data.name,
              hasGender: !!data.gender,
              hasLookingFor: !!data.looking_for,
              hasDateOfBirth: !!data.date_of_birth,
              hasPhotos: !!(data.photos && data.photos.length > 0)
            })
            console.warn('[AuthContext] Full profile data:', data)
          }
          
          setProfile(data)
        } else {
          console.error('[AuthContext] ❌ Profile data is null!')
          setProfile(null)
        }
      }
    } catch (error) {
      console.error('[AuthContext] Exception loading profile:', error)
      console.error('[AuthContext] Error details:', {
        message: error.message,
        stack: error.stack
      })
      setProfile(null)
    } finally {
      if (!silent) {
        console.log('[AuthContext] Setting loading to false')
        setLoading(false)
      }
      profileBootstrapDoneRef.current = true
    }
  }

  const signUp = async (email, password, onboardingData) => {
    try {
      const normalizedEmail = email.toLowerCase().trim()
      console.log('[AuthContext.signUp] Attempting signUp for:', normalizedEmail)
      const startTime = Date.now()
      
      // Выполняем signUp без таймаута - позволяем запросу завершиться естественным образом
      // Если запрос действительно зависнет, браузер сам покажет ошибку
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/app`,
          data: {
            // Дополнительные данные для профиля
            name: onboardingData?.name || '',
          }
        },
      })

      const elapsed = Date.now() - startTime
      console.log('[AuthContext.signUp] Request completed in', elapsed, 'ms')

      if (authError) {
        console.error('[AuthContext.signUp] Auth error:', {
          message: authError.message,
          status: authError.status,
          name: authError.name,
          code: authError.code,
          full: authError
        })
        
        // Более понятное сообщение об ошибке
        let errorMessage = authError.message
        if (authError.message?.includes('database') || authError.status === 500) {
          errorMessage = 'Database error saving new user. Please try again.'
        }
        
        return { user: null, error: errorMessage }
      }

      if (!authData.user) {
        console.warn('[AuthContext.signUp] No user in response')
        return { user: null, error: 'User was not created' }
      }

      console.log('[AuthContext.signUp] ✅ User created successfully, ID:', authData.user.id)
      
      // Не проверяем профиль здесь - это может зависнуть
      // Профиль создастся триггером или позже через saveOnboardingToProfile
      // Возвращаем успех сразу, чтобы не блокировать UI
      
      return { user: authData.user, error: null }
    } catch (error) {
      console.error('Sign up exception:', error)
      return { user: null, error: error.message || 'Error creating account' }
    }
  }

  const signIn = async (email, password) => {
    try {
      const normalizedEmail = email.toLowerCase().trim()
      console.log('[AuthContext.signIn] Attempting signIn for:', normalizedEmail)

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      })

      if (error) {
        console.error('[AuthContext.signIn] Auth error:', {
          message: error.message,
          status: error.status,
          name: error.name,
          code: error.code,
          full: error,
        })
        return { user: null, error: error.message }
      }

      if (!data?.user) {
        console.warn('[AuthContext.signIn] No user in response')
        return { user: null, error: 'User was not found' }
      }

      console.log('[AuthContext.signIn] Success, user ID:', data.user.id)

      // Загружаем профиль в фоне, не блокируя навигацию
      loadProfile(data.user.id, { silent: true }).catch((err) => {
        console.error('[AuthContext.signIn] Error loading profile:', err)
      })

      return { user: data.user, error: null }
    } catch (error) {
      console.error('[AuthContext.signIn] Exception:', error)
      const message = error?.message || 'Error signing in. Please try again.'
      return { user: null, error: message }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      profileBootstrapDoneRef.current = false
      setUser(null)
      setProfile(null)
      window.location.href = '/'
    } catch (error) {
      console.error('Sign out error:', error)
      throw error
    }
  }

  const updateProfile = async (userId, updates) => {
    try {
      // Сначала проверяем, существует ли профиль
      const { data: existing, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single()

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 = not found, это нормально, создадим профиль
        console.error('Error checking profile:', checkError)
      }

      // Если профиля нет, создаем его
      if (!existing) {
        const { data: userData } = await supabase.auth.getUser()
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: userData?.user?.email || '',
            ...updates,
          })

        if (insertError) {
          console.error('Error creating profile:', insertError)
          throw insertError
        }
      } else {
        // Обновляем существующий профиль
        const { data, error } = await supabase
          .from('profiles')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId)
          .select()
          .single()

        if (error) throw error
        setProfile(data)
        return { data, error: null }
      }

      // Загружаем обновленный профиль
      return await loadProfile(userId, { silent: true })
    } catch (error) {
      console.error('Update profile error:', error)
      return { data: null, error: error.message }
    }
  }

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Reset password error:', error)
      return { error: error.message }
    }
  }

  // Обновление пароля после перехода по ссылке из письма (password recovery)
  const updatePassword = async (newPassword) => {
    try {
      console.log('[AuthContext.updatePassword] Attempting to update password...')
      
      // Проверяем что есть recovery сессия
      const { data: sessionCheck } = await supabase.auth.getSession()
      if (!sessionCheck?.session) {
        console.error('[AuthContext.updatePassword] No recovery session found')
        return { user: null, error: 'Password reset link is invalid or has expired. Please request a new one.' }
      }

      console.log('[AuthContext.updatePassword] Recovery session found, updating password...')
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) {
        console.error('[AuthContext.updatePassword] Update error:', error)
        return { user: null, error: error.message }
      }

      if (!data?.user) {
        console.warn('[AuthContext.updatePassword] No user in response')
        return { user: null, error: 'Password was not updated' }
      }

      console.log('[AuthContext.updatePassword] ✅ Password updated successfully, user ID:', data.user.id)
      
      // Обновляем состояние
      if (data.user) {
        setUser(data.user)
        // Загружаем профиль
        await loadProfile(data.user.id, { silent: true })
      }

      return { user: data.user, error: null }
    } catch (error) {
      console.error('[AuthContext.updatePassword] Exception:', error)
      return { user: null, error: error.message || 'Error updating password' }
    }
  }

  const checkEmailExists = async (email) => {
    const normalized = email.toLowerCase().trim()
    const timeoutMs = 15000 // Увеличено до 15 секунд

    try {
      console.log('[checkEmailExists] Checking email:', normalized)

      // RPC функция в Supabase (SECURITY DEFINER) смотрит на email в таблице profiles
      const rpcPromise = supabase
        .rpc('check_email_exists', { check_email: normalized })
        .then((result) => {
          console.log('[checkEmailExists] RPC response:', result)
          return result
        })

      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          console.warn(
            '[checkEmailExists] Timed out after',
            timeoutMs,
            'ms (treating as NEW user)',
          )
          // В случае таймаута считаем, что email новый, чтобы не блокировать онбординг
          resolve({ data: false, error: null })
        }, timeoutMs)
      })

      const { data, error } = await Promise.race([rpcPromise, timeoutPromise])

      if (error) {
        console.warn('[checkEmailExists] RPC error, treating as NEW user:', error)
        return { exists: false, error: null }
      }

      const exists = data === true
      console.log('[checkEmailExists] Result for', normalized, ':', exists ? 'EXISTS' : 'NEW')
      return { exists, error: null }
    } catch (err) {
      console.warn('[checkEmailExists] Exception, treating as NEW user:', err)
      return { exists: false, error: null }
    }
  }

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    checkEmailExists,
    refreshProfile: () => (user ? loadProfile(user.id, { silent: true }) : Promise.resolve()),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
